import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Icon, Snackbar, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import { Card, CardContent, Typography, Grid, Paper, useTheme, useMediaQuery } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { addRiskMatrix, getRiskMatrices, deleteRiskMatrixById } from "services/api";
import ConfirmationDialog from "components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import AddRiskMatrixDialog from "components/AddRiskMatrixDialog";
import BubbleChart from "examples/Charts/BubbleChart";
import Tooltip from "@mui/material/Tooltip";
import { fetchRiskMatrices } from "services/utils";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";

function RiskMatrixView({ canEdit }) {
  const { employee } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [riskMatrices, setRiskMatrices] = useState([]);
  const [selectedRiskMatrix, setSelectedRiskMatrix] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  // State for  Risk Matrix Dialog (opening and closing the risk matrix dialog for both add and edit modes)
  const [openRiskMatrixDialog, setOpenRiskMatrixDialog] = useState(false);
  // State for Editing Risk Mat.
  const [editedRiskMatrix, setEditedRiskMatrix] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [confOpen, setConfOpen] = useState(false);
  const [riskMatrixChart, setRiskMatrixChart] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const snackbarRef = useRef();

  //////Opening Add Risk Matrix Dialog
  const handleOpenRiskMatrixDialog = () => {
    setSelectedRiskMatrix(null);
    setOpenRiskMatrixDialog(true); //opening the dialog for the user to see
  };

  const handleCloseRiskMatrixDialog = () => {
    setOpenRiskMatrixDialog(false);
    setSelectedRiskMatrix(null);
  };

  ///////////Opening Edit Risk Matrix Dialog//////
  const handleOpenEditDialog = (riskMatrix) => {
    setEditedRiskMatrix({ ...riskMatrix }); // Copy the risk matrix data
    setSelectedRiskMatrix(riskMatrix);

    setOpenRiskMatrixDialog(true); //opening the dialog for the user to see
  };

  const handleAddRiskMatrix = async (data, addKey) => {
    try {
      await addRiskMatrix(data, addKey);
      // await fetchRiskMatrices(); // ← Refresh list & chart
      await fetchRiskMatrices(setRiskMatrices, setRiskMatrixChart);
      setOpenRiskMatrixDialog(false);
    } catch (error) {
      snackbarRef.current?.showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const companyId = employee.companyId;
    setCompanyId(companyId);
    setLoading(true);

    fetchRiskMatrices(setRiskMatrices, setRiskMatrixChart).finally(() => {
      // fetchRiskMatrices().finally(() => {

      setLoading(false);
    });
  }, [employee.companyId]);

  const handleDeleteRiskMatrix = async () => {
    if (!selectedRiskMatrix) return;
    setConfOpen(true);
  };

  return (
    <MDBox p={3}>
      <MDTypography variant="h4" mb={3} textAlign="center">
        {t("admin.strategicInfo.riskManagement.title")}
      </MDTypography>

      {canEdit && <AddFloatingButton onClick={handleOpenRiskMatrixDialog} />}

      {
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={12} lg={8} md={6}>
            {riskMatrixChart?.length > 0 ? (
              <BubbleChart
                title={t("admin.dashboard.riskMatrix")}
                height={400}
                processedData={riskMatrixChart}
                labels={{
                  x: t("admin.strategicInfo.riskManagement.impact"),
                  y: t("admin.strategicInfo.riskManagement.likelihood"),
                }}
              />
            ) : (
              <Card sx={{ height: "100%" }}>
                <MDBox py={2} pr={2} pl={2}>
                  {/* Title */}
                  <MDBox mb={2}>
                    <MDTypography variant="h6" align="center">
                      {t("admin.dashboard.riskMatrix")}
                    </MDTypography>
                  </MDBox>

                  <MDBox height={350} display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress size={20} />
                  </MDBox>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      }
      {
        <Box mt={4}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : riskMatrices.length === 0 ? (
            <Box display="flex" justifyContent="center" p={2}>
              <Typography variant="body2" color="textSecondary">
                {t("admin.strategicInfo.riskManagement.noDataAvailable")}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              {/* <Table sx={{ width: "100%" }}> */}
              <Table sx={{ minWidth: 600 }}>
                <TableBody>
                  {/* HEADER ROW AS BODY ROW */}
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      {t("admin.strategicInfo.riskManagement.name")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "10%" }}>
                      {t("admin.strategicInfo.riskManagement.impact")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "10%" }}>
                      {t("admin.strategicInfo.riskManagement.likelihood")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      {t("admin.strategicInfo.riskManagement.owner")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                      {t("admin.strategicInfo.riskManagement.mitigation")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "10%" }}>
                      {t("admin.strategicInfo.riskManagement.actions")}
                    </TableCell>
                  </TableRow>

                  {/* DATA ROWS */}
                  {riskMatrices.map((matrix) => (
                    <TableRow key={matrix.businessDrivenId}>
                      <TableCell sx={{ wordBreak: "break-word" }}>{matrix.matrixName}</TableCell>
                      <TableCell>{matrix.impact}</TableCell>
                      <TableCell>{matrix.likelihood}</TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }}>{matrix.riskOwner}</TableCell>

                      <TableCell
                        onClick={() => setExpanded((prev) => !prev)}
                        sx={{
                          cursor: { xs: "pointer", sm: "default" }, // clickable only on xs
                        }}
                      >
                        <Box
                          sx={{
                            whiteSpace: {
                              xs: expanded ? "normal" : "nowrap", // ellipsis if NOT expanded on xs
                              sm: "normal", // always wrap on sm+
                            },
                            overflow: {
                              xs: expanded ? "visible" : "hidden", // hide overflow unless expanded
                              sm: "visible", // always visible on sm+
                            },
                            textOverflow: {
                              xs: expanded ? "unset" : "ellipsis", // ellipsis on xs only if NOT expanded
                              sm: "unset", // no ellipsis on sm+
                            },
                            maxWidth: {
                              xs: expanded ? "none" : 120, // limit width on xs only if NOT expanded
                              sm: "none", // no width limit on sm+
                            },
                            wordBreak: "break-word",
                            display: "inline-block", // needed for ellipsis to work
                          }}
                        >
                          {matrix.riskMitigation}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Button
                          size="small"
                          color="primary"
                          disabled={!canEdit}
                          onClick={() => handleOpenEditDialog(matrix)}
                        >
                          {t("admin.strategicInfo.riskManagement.edit")}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={!canEdit}
                          onClick={() => {
                            setSelectedRiskMatrix(matrix);
                            setConfOpen(true);
                          }}
                          sx={{ color: "error.main" }}
                        >
                          {t("admin.strategicInfo.riskManagement.delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      }

      {/* Risk Matrix Dialog */}
      <AddRiskMatrixDialog
        open={openRiskMatrixDialog}
        onClose={handleCloseRiskMatrixDialog}
        riskMatrix={selectedRiskMatrix}
        handleSubmit={handleAddRiskMatrix}
        handleDelete={handleDeleteRiskMatrix}
        compId={companyId}
      />

      <ConfirmationDialog
        open={confOpen}
        onClose={() => {
          setConfOpen(false);
        }}
        onConfirm={async () => {
          try {
            await deleteRiskMatrixById(selectedRiskMatrix.businessDrivenId);
            // await fetchRiskMatrices();
            await fetchRiskMatrices(setRiskMatrices, setRiskMatrixChart);
            setOpenRiskMatrixDialog(false);
          } catch (error) {
            console.error("Error deleting risk matrix:", error);
          }
          setConfOpen(false);
        }}
      />

      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </MDBox>
  );
}

RiskMatrixView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default RiskMatrixView;
